import { FeaturedTariffPreviewMode, type Scheduling, type TariffGroup } from '@src/core/buyerApiClient';
import { getSchedulingsTariffPreview, isFeaturedTariffCandidate } from '@src/domains/tariffs';
import type { PreviewPrice } from '@src/domains/products/features/product-booking/core/computeUnitPrice';

// Resolves the price a featured-tariff mode points at: from the month's
// schedulings while there are bookable days, from the product's default tariff
// group otherwise. Null when the mode matches no tariff.
export function resolvePreviewPrice(
  mode: FeaturedTariffPreviewMode,
  monthSchedulings: Scheduling[],
  defaultTariffGroup: TariffGroup | undefined,
): PreviewPrice | null {
  if (monthSchedulings.length > 0) {
    const cents = getSchedulingsTariffPreview(monthSchedulings, mode);
    // It returns a bare number: the winning tariff is lost in the flattening, so
    // there is no old price to pair with it.
    return cents == null ? null : { cents, oldCents: null };
  }

  // No bookable day this month — availability that only starts later, or a
  // product sold without a date. Same filter, over the raw tariffs.
  const candidates = (defaultTariffGroup?.tariffs ?? []).filter(
    (tariff) => isFeaturedTariffCandidate(tariff, mode) && typeof tariff.priceCents === 'number',
  );
  if (candidates.length === 0) return null;

  const prices = candidates.map((tariff) => tariff.priceCents);
  const cents = mode === FeaturedTariffPreviewMode.Highest ? Math.max(...prices) : Math.min(...prices);
  // `priceCents` varies per schedule hour while `oldPriceCents` is static per
  // tariff, so pairing them is only sound with a single surviving candidate —
  // the same guard the default-group walk uses. Dropping it would erase the
  // struck-through price of single-tariff products.
  const oldCents = candidates.length === 1 ? candidates[0]!.oldPriceCents ?? null : null;

  return { cents, oldCents };
}
